import { animated, useTrail } from "@react-spring/web";
import { FC } from "react";
import { useLocation, useNavigate } from "react-router";

import { APP_BASE_HREF } from "../App";
import styles from "../App.module.css";
import AppTile from "../components/AppTile";
import { useBreadcrumbs } from "../hooks/breadcrumbs";
import backArrow from "../images/backArrow.svg?url";
import { useOrganisationManagers, useUserHasRole } from "../queries/general";

import { Tile, tiles } from "./tiles";

interface AnimatedTilesProps {
  tiles: Tile[];
  navigate: (path: string) => void;
}

const AnimatedTiles: FC<AnimatedTilesProps> = ({ tiles, navigate }) => {
  const handleLink = (linksToObject: { path: string; external: boolean }) => {
    if (linksToObject.external === true) {
      window.location.href = linksToObject.path;
    } else {
      navigate(linksToObject.path);
    }
  };

  const trail = useTrail(tiles.length, {
    from: { opacity: 0, x: -5 },
    opacity: 1,
    x: 0,
  });

  const isManager = useUserHasRole("manager");
  const managers = useOrganisationManagers();
  const managerNames = managers
    .map((manager) => manager.firstName + " " + manager.lastName)
    .join(", ");

  return (
    <div className={styles.Apps}>
      {tiles.map((appTile, idx) => (
        <animated.div
          style={{
            opacity: trail[idx].opacity,
            transform: trail[idx].x.to((x: number) => `translate3d(${x}%,0,0)`),
          }}
          key={appTile.title + idx + ""}
        >
          <AppTile
            handleClick={() => {
              handleLink({
                external: appTile.linksToExternal ? true : false,
                path: appTile.linksToExternal ? appTile.linksTo : APP_BASE_HREF + appTile.linksTo,
              });
            }}
            title={appTile.title}
            icon={appTile.icon}
            readonly={appTile.disabled || (appTile.requiresManager && !isManager) ? true : false}
            requiredRoles={[]}
            tooltip={
              appTile.requiresManager && !isManager
                ? `You do not have sufficient rights to manage ${appTile.title.toLowerCase()}. Please contact ${
                    managerNames || "the Service Desk."
                  }`
                : undefined
            }
          />
        </animated.div>
      ))}
    </div>
  );
};

const Home = () => {
  const crumbs = useBreadcrumbs();
  const location = useLocation();
  const navigate = useNavigate();

  let currentRelativeUrl = location.pathname.slice(APP_BASE_HREF.length);
  // Remove trailing slash if present
  if (currentRelativeUrl.endsWith("/")) {
    currentRelativeUrl = currentRelativeUrl.slice(0, -1);
  }

  const theTiles = (tiles[currentRelativeUrl] || []).slice().concat(
    crumbs.length > 1
      ? [
          {
            title: "Back",
            icon: backArrow,
            linksTo: crumbs[crumbs.length - 2].url,
          },
        ]
      : []
  );

  return (
    <div>
      <div className="container">
        <div className="row" key={currentRelativeUrl}>
          <AnimatedTiles tiles={theTiles} navigate={navigate} />
        </div>
      </div>
    </div>
  );
};

export default Home;
